import dayjs from 'dayjs';
import Emitter from 'component-emitter';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from '@tanstack/react-router';

import { ConfigContext } from 'context/config';

import { useAspenLogger } from 'hooks/useAspenLogger';
import { aspenSockets } from 'modules/aspenSockets';
import { getDurationOrNull } from 'modules/time';
import {
  formatIceServers,
  RTCTools,
  isRecoverablePeerConnection,
  isActivePeerConnection,
  formatErrorMessage,
  ErrorOrigin,
} from 'modules/webRTC';
import { registerCleanup } from 'modules/globalCleanup';

import { AppDeviceLeaf } from 'types/common/leaf';

const ICE_GATHERING_TIMEOUT = 2500 as const;
const BATTERY_CAM_TIME_LIMIT = 5 * 60 * 1000; // 5 minutes

const RTCPeerConnections = Emitter(new Map<string, RTCPeerConnection>());

interface WebRTCContext {
  peerConnections: typeof RTCPeerConnections;
  connect: (params: {
    leaf: AppDeviceLeaf;
    peerConnection?: RTCPeerConnection;
    trigger: ConnectionTrigger;
  }) => Promise<void>;
  close: (params: { leafId: AppDeviceLeaf['id'] }) => void;
}

export const WebRTCContext = createContext<WebRTCContext>({} as WebRTCContext);

interface WebRTCProviderProps {
  children: React.ReactNode;
}

/*
    WebRTC Context manages peer connections for smart camera video streaming.
    
    The app initiates connections and cameras provide answers. This context handles:
    - Connection lifecycle management with state-based guards
    - Automatic cleanup and resource management
    - Recovery logic for disconnected connections
    - Comprehensive logging for analytics

    Connection Flow:
    1. Create RTCPeerConnection and store in persistent map
    2. Fetch ICE servers from signaling server
    3. Add audio/video transceivers with codec preferences
    4. Create offer and set as local description
    5. Gather ICE candidates (with timeout fallback)
    6. Send offer to signaling server and await answer
    7. Set remote description and establish media stream

    Connection States:
    - Active (connecting/connected): Protected from interference
    - Recoverable (disconnected): Reuse existing connection via reconnect
    - Failed/Closed: Reset and create new connection
    - Battery devices: Auto-disconnect after 5 minutes
*/

export const WebRTCProvider = ({ children }: WebRTCProviderProps) => {
  const { baseUrl } = useContext(ConfigContext);
  const { pathname } = useLocation();
  const { logAspenEvent } = useAspenLogger();

  // Register global cleanup for WebRTC connections
  useEffect(() => {
    registerCleanup('webrtc', () => {
      // Close all connections when page is being unloaded
      RTCPeerConnections.forEach((_value, key) => {
        dispatchClose({ leafId: key });
      });
    });
  }, []);

  interface InitNewConnectionParams {
    leaf: AppDeviceLeaf;
    trigger: ConnectionTrigger;
  }

  const initNewConnection = async ({ leaf, trigger }: InitNewConnectionParams) => {
    const { fetchStreamInfo } = aspenSockets({ baseUrl });

    const { sendOffer } = RTCTools({
      baseUrl,
      leaf,
    });

    const peerConnection = new RTCPeerConnection();
    peerConnection.timers = {
      // Initialize properties we'll definitely use
      totalIceGatheringTime: 0,
      iceGatheringAttempts: 0,
    };
    peerConnection.disconnectionCount = 0;
    peerConnection.isRecovering = false;
    RTCPeerConnections.set(leaf.id, peerConnection);

    // Store trigger type
    peerConnection.trigger = trigger;

    // Notify RTC components of the new connection
    RTCPeerConnections.emit('newconnection', leaf.id);

    try {
      // Dispatch loading event
      peerConnection.dispatchEvent(new CustomEvent('loadingchange', { detail: { loading: true } }));
      // Fetch signaling server info
      const { servers } = await fetchStreamInfo({
        leafId: leaf.id,
        leafType: leaf.type,
      });

      // Create a new connection
      const configuration = {
        iceServers: formatIceServers(servers),
      };
      peerConnection.setConfiguration(configuration);

      // Add transceivers for audio and video
      const videoTransceiver = peerConnection.addTransceiver('video', { direction: 'recvonly' });
      peerConnection.addTransceiver('audio', { direction: 'sendrecv' });

      // Set codec preference to H264 to support wireless doorbell
      const codecH264 = RTCRtpReceiver.getCapabilities('video')?.codecs?.find(
        (codec) => codec?.mimeType === 'video/H264',
      );

      if (codecH264) {
        videoTransceiver.setCodecPreferences([codecH264]);
      }

      // Set offer to local description
      const offer = await peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });

      // Trigger ICE geathering
      await peerConnection.setLocalDescription(offer);

      peerConnection.onicegatheringstatechange = async (event: Event) => {
        const connectionFromEvent = event.target as RTCPeerConnection;
        const { iceGatheringState } = connectionFromEvent;

        switch (iceGatheringState) {
          case 'gathering':
            // Increment ICE gathering attempt count
            connectionFromEvent.timers.iceGatheringAttempts += 1;

            // Only set start time if this is the first gathering attempt
            if (!connectionFromEvent.timers.iceGatheringStart) {
              connectionFromEvent.timers.iceGatheringStart = dayjs().valueOf();
            }

            // Reset end time so it can be set again for this attempt
            delete connectionFromEvent.timers.iceGatheringEnd;

            setTimeout(async () => {
              // Store iceGatheringEnd time and accumulate total gathering time
              if (!connectionFromEvent.timers.iceGatheringEnd) {
                connectionFromEvent.timers.iceGatheringEnd = dayjs().valueOf();
                const gatheringTime =
                  connectionFromEvent.timers.iceGatheringEnd - (connectionFromEvent.timers.iceGatheringStart || 0);
                connectionFromEvent.timers.totalIceGatheringTime += gatheringTime;
              }

              try {
                await sendOffer({ peerConnection: connectionFromEvent });
              } catch (err) {
                dispatchError({ leaf, error: err as Error, origin: 'signaling' });
              }
            }, ICE_GATHERING_TIMEOUT);
            break;
          case 'complete': {
            if (!connectionFromEvent.timers.iceGatheringEnd) {
              connectionFromEvent.timers.iceGatheringEnd = dayjs().valueOf();
              const gatheringTime =
                connectionFromEvent.timers.iceGatheringEnd - (connectionFromEvent.timers.iceGatheringStart || 0);
              connectionFromEvent.timers.totalIceGatheringTime += gatheringTime;
            }

            try {
              await sendOffer({ peerConnection: connectionFromEvent });
            } catch (err) {
              dispatchError({ leaf, error: err as Error, origin: 'signaling' });
            }
            break;
          }
          default:
        }
      };

      peerConnection.onnegotiationneeded = async (event: Event) => {
        const connectionFromEvent = event.target as RTCPeerConnection;
        // This automatically handles the local description based on the current signaling state
        // See more: https://developer.mozilla.org/en-US/docs/Web/API/WebRTC_API/Perfect_negotiation
        if (
          connectionFromEvent.connectionState !== 'new' &&
          !connectionFromEvent.negotiationInProgress &&
          !connectionFromEvent.offerInProgress
        ) {
          connectionFromEvent.negotiationInProgress = true;
          try {
            await connectionFromEvent.setLocalDescription();
          } catch (err) {
            dispatchError({ leaf, error: err as Error, origin: 'webrtc' });
            return;
          }
          try {
            await sendOffer({ peerConnection: connectionFromEvent });
          } catch (err) {
            dispatchError({ leaf, error: err as Error, origin: 'signaling' });
          } finally {
            connectionFromEvent.negotiationInProgress = false;
          }
        }
      };

      peerConnection.onconnectionstatechange = (event: Event) => {
        const connectionFromEvent = event.target as RTCPeerConnection;
        const { connectionState } = connectionFromEvent;

        if (connectionState === 'connecting') {
          // Store connecting start time
          connectionFromEvent.timers.connectingStart = dayjs().valueOf();
        }

        if (connectionState === 'disconnected') {
          // Track disconnection count and set recovery flag
          connectionFromEvent.disconnectionCount += 1;
          connectionFromEvent.isRecovering = true;
        }

        if (connectionState === 'connected') {
          // Set battery timeout
          setBatteryTimeout({ leaf });

          // Store connecting end time
          connectionFromEvent.timers.connectingEnd = dayjs().valueOf();

          // Store connection start time only if not already set (first connection)
          if (!connectionFromEvent.timers.connectionStart) {
            connectionFromEvent.timers.connectionStart = dayjs().valueOf();
          }

          // Only log stream start if this is not a recovery from disconnection
          if (!connectionFromEvent.isRecovering) {
            // Log iot.web.stream.start BDP event
            logAspenEvent({
              log_id: 'iot.web.stream.start',
              payload: {
                device_id: leaf.id,
                conn_time_ms: getDurationOrNull({
                  startTime: connectionFromEvent.timers.connectingStart,
                  endTime: connectionFromEvent.timers.connectingEnd,
                }),
                ice_gathering_time_ms: connectionFromEvent.timers.totalIceGatheringTime,
                signaling_server_time_ms: getDurationOrNull({
                  startTime: connectionFromEvent.timers.signalingStart,
                  endTime: connectionFromEvent.timers.signalingEnd,
                }),
                trigger: connectionFromEvent.trigger,
                url: pathname,
                webrtc_session_id: connectionFromEvent.sessionId,
              },
            });
          }

          // Reset recovery flag after successful connection
          connectionFromEvent.isRecovering = false;
        }

        if (connectionState === 'failed') {
          // Store connecting end time if its not set yet
          if (!connectionFromEvent.timers.connectingEnd) {
            connectionFromEvent.timers.connectingEnd = dayjs().valueOf();
          }

          const failureError = connectionFromEvent?.error || new Error('Connection failed');
          const formattedError = formatErrorMessage(failureError, 'webrtc');

          logAspenEvent({
            log_id: 'iot.web.stream.failed',
            payload: {
              device_id: leaf.id,
              conn_time_ms: getDurationOrNull({
                startTime: connectionFromEvent.timers.connectingStart,
                endTime: connectionFromEvent.timers.connectingEnd,
              }),
              ice_gathering_time_ms: connectionFromEvent.timers.totalIceGatheringTime,
              signaling_server_time_ms: getDurationOrNull({
                startTime: connectionFromEvent.timers.signalingStart,
                endTime: connectionFromEvent.timers.signalingEnd,
              }),
              trigger: connectionFromEvent.trigger,
              error: formattedError,
              url: pathname,
              webrtc_session_id: connectionFromEvent.sessionId,
            },
          });

          // Reset recovery flag on failure
          connectionFromEvent.isRecovering = false;

          // Auto-close failed connection for better resource management
          dispatchClose({ leafId: leaf.id });
        }

        if (connectionState === 'closed') {
          // Store connection end time
          connectionFromEvent.timers.connectionEnd = dayjs().valueOf();

          // Log iot.web.stream.end BDP event
          logAspenEvent({
            log_id: 'iot.web.stream.end',
            payload: {
              device_id: leaf.id,
              duration_ms: getDurationOrNull({
                startTime: connectionFromEvent.timers.connectionStart,
                endTime: connectionFromEvent.timers.connectionEnd,
              }),
              url: pathname,
              webrtc_session_id: connectionFromEvent.sessionId,
              disconnection_count: connectionFromEvent.disconnectionCount,
            },
          });

          RTCPeerConnections.delete(leaf.id);
        }
      };

      peerConnection.ontrack = (event: RTCTrackEvent) => {
        const connectionFromEvent = event.target as RTCPeerConnection;
        const [stream] = event.streams;

        connectionFromEvent.mediaStream = stream;
      };
    } catch (err) {
      dispatchError({ leaf, error: err as Error, origin: 'webrtc' });
    }
  };

  interface DispatchErrorParams {
    leaf: AppDeviceLeaf;
    error: Error;
    origin?: ErrorOrigin;
  }

  const dispatchError = ({ leaf, error, origin }: DispatchErrorParams) => {
    const peerConnection = RTCPeerConnections.get(leaf.id);

    if (peerConnection) {
      peerConnection.error = error;
      peerConnection.dispatchEvent(new CustomEvent('error', { detail: error }));

      const formattedError = formatErrorMessage(error, origin);

      // Log iot.web.stream.failed BDP event for manual errors
      logAspenEvent({
        log_id: 'iot.web.stream.failed',
        payload: {
          device_id: leaf.id,
          conn_time_ms: getDurationOrNull({
            startTime: peerConnection.timers.connectingStart,
            endTime: peerConnection.timers.connectingEnd,
          }),
          ice_gathering_time_ms: peerConnection.timers.totalIceGatheringTime,
          signaling_server_time_ms: getDurationOrNull({
            startTime: peerConnection.timers.signalingStart,
            endTime: peerConnection.timers.signalingEnd,
          }),
          trigger: peerConnection.trigger,
          error: formattedError,
          url: pathname,
          webrtc_session_id: peerConnection.sessionId,
        },
      });

      // Auto-close errored connection for better resource management
      dispatchClose({ leafId: leaf.id });
    }
  };

  interface DispatchCloseParams {
    leafId: AppDeviceLeaf['id'];
  }

  const dispatchClose = ({ leafId }: DispatchCloseParams) => {
    const peerConnection = RTCPeerConnections.get(leafId);

    if (peerConnection) {
      // Store connection end time
      if (!peerConnection.timers.connectionEnd) {
        peerConnection.timers.connectionEnd = dayjs().valueOf();
      }

      // Log iot.web.stream.end BDP event for manual closes
      logAspenEvent({
        log_id: 'iot.web.stream.end',
        payload: {
          device_id: leafId,
          duration_ms: getDurationOrNull({
            startTime: peerConnection.timers.connectionStart,
            endTime: peerConnection.timers.connectionEnd,
          }),
          url: pathname,
          webrtc_session_id: peerConnection.sessionId,
          disconnection_count: peerConnection.disconnectionCount,
        },
      });

      // Dispatch close event for useWebRTC hook
      peerConnection.dispatchEvent(new Event('close'));

      // Close the connection
      peerConnection.close();

      // Remove from connections map
      RTCPeerConnections.delete(leafId);
    }
  };

  interface SetBatteryTimeoutParams {
    leaf: AppDeviceLeaf;
  }

  const setBatteryTimeout = ({ leaf }: SetBatteryTimeoutParams) => {
    const peerConnection = RTCPeerConnections.get(leaf.id);
    const isBatteryDevice = leaf?.state?.battery;

    if (peerConnection && isBatteryDevice) {
      // Remove existing timeout listener
      clearTimeout(peerConnection.batteryTimeoutListener);

      peerConnection.batteryTimeoutListener = setTimeout(() => {
        // Set battery timeout to true and dispatch error event
        peerConnection.batteryTimeout = true;
        dispatchError({ leaf, error: new Error('BatteryTimeout'), origin: 'device' });
      }, BATTERY_CAM_TIME_LIMIT);
    }
  };

  interface ReconnectParams {
    leaf: AppDeviceLeaf;
    trigger: ConnectionTrigger;
  }

  const reconnect = async ({ leaf, trigger }: ReconnectParams) => {
    const peerConnection = RTCPeerConnections.get(leaf.id);

    if (peerConnection) {
      // Store trigger type
      peerConnection.trigger = trigger;

      const { sendOffer } = RTCTools({
        baseUrl,
        leaf,
      });

      // Dispatch loading event
      peerConnection.dispatchEvent(new CustomEvent('loadingchange', { detail: { loading: true } }));

      await peerConnection.setLocalDescription();
      try {
        await sendOffer({ peerConnection });
      } catch (err) {
        dispatchError({ leaf, error: err as Error, origin: 'signaling' });
      }
    }
  };

  const connect: WebRTCContext['connect'] = async ({ leaf, peerConnection, trigger }) => {
    if (isActivePeerConnection(peerConnection)) {
      return;
    }

    if (isRecoverablePeerConnection(peerConnection)) {
      await reconnect({ leaf, trigger });
    } else {
      dispatchClose({ leafId: leaf.id });
      await initNewConnection({ leaf, trigger });
    }
  };

  const close: WebRTCContext['close'] = ({ leafId }) => {
    dispatchClose({ leafId });
  };

  const contextValue = useMemo(
    () => ({ peerConnections: RTCPeerConnections, connect, close }),
    [RTCPeerConnections, connect, close],
  );

  return <WebRTCContext.Provider value={contextValue}>{children}</WebRTCContext.Provider>;
};
